import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import SelectDropdown from "../../library/SelectDropdown";
import NumberInput from "../../library/NumberInput";
import DateInput from "../../library/DateInput";
import TextInput from "../../library/TextInput";
import Checkbox from "../../library/Checkbox";
import PositiveButton from "../../library/PositiveButton";
import Button from "../../library/Button";
import CommonButton from "../../common/Button/Button";
import { FinishFirstAidCheck, StartFirstAidCheck, SubmitFirstAidCheck } from "../../../services/FirstAidBoxService";
import {
    invalidateFirstAidBoxQueries,
    useFirstAidBox,
    useFirstAidCatalog
} from "../../../query/hooks/QueryHooksFirstAidBox";
import { CONDITION_LABELS, EVENT_TYPES, EXPIRING_SOON_DAYS, ITEM_CONDITION_OPTIONS, OHS_BASE_PATH } from "./ohsConstants";
import { getItemStatuses, getOhsErrorMessage } from "./ohsUtils";
import {
    CategoryHeading,
    CheckItemName,
    CheckItemRow,
    ConditionBadge,
    ConfirmationPanel,
    FormGrid,
    MutedText,
    SectionCard,
    SectionHeader,
    SectionTitle,
    TagResultMessage,
    TagStepStack,
    WrappingButtonGroup
} from "./Ohs.styles";
import ConditionBadges from "./ConditionBadges";

const CHECK_STEPS = Object.freeze({ tag: "tag", decision: "decision", full: "full" });

/**
 * Regular (monthly) check, or a manager check (?managerCheck=true), of one box in two steps:
 * 1. Enter the tag on the box - saved straight away and compared with the one on record.
 * 2. If it matched, finish on the intact tag (last known stock) or continue; otherwise (or on
 *    continue) do the full item-by-item check and fit a new tag.
 * @param {Object} props
 * @param {string} props.boxId Box being checked.
 */
function BoxCheckForm({ boxId }) {
    const history = useHistory();
    const isManagerCheck = new URLSearchParams(useLocation().search).get("managerCheck") === "true";
    const eventType = isManagerCheck ? EVENT_TYPES.managerCheck : EVENT_TYPES.firstAiderCheck;

    const { data: box, isLoading: isBoxLoading } = useFirstAidBox(boxId);
    const { data: catalog, isLoading: isCatalogLoading } = useFirstAidCatalog();
    const [checkStep, setCheckStep] = useState(CHECK_STEPS.tag);
    const [startedCheck, setStartedCheck] = useState(null);
    const [itemEntries, setItemEntries] = useState({});
    const [tagSerialFound, setTagSerialFound] = useState("");
    const [isTagMissing, setIsTagMissing] = useState(false);
    const [newTagSerial, setNewTagSerial] = useState("");
    const [tagErrors, setTagErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittedCheck, setSubmittedCheck] = useState(null);

    // Start every row as fully stocked, carrying the last known expiry date forward since it rarely changes.
    useEffect(() => {
        if (!box || !catalog.length) {
            return;
        }
        const previousRowsByItem = new Map(box.currentStock.map((checkRow) => [checkRow.box_item_id, checkRow]));
        const initialEntries = {};
        catalog.forEach((catalogItem) => {
            initialEntries[catalogItem.id] = {
                isDamaged: false,
                quantity: catalogItem.expected_quantity,
                expiryDate: previousRowsByItem.get(catalogItem.id)?.expiry_date || "",
                notes: "",
            };
        });
        setItemEntries(initialEntries);
    }, [box, catalog]);

    // The first aider's latest report per item - shown during a manager check so it can be verified.
    const firstAiderRowsByItem = useMemo(
        () => new Map((box?.lastFirstAiderCheck || []).map((checkRow) => [checkRow.box_item_id, checkRow])),
        [box]
    );

    const catalogByCategory = useMemo(() => {
        const categoryGroups = new Map();
        catalog.forEach((catalogItem) => {
            const category = catalogItem.category || "Other";
            if (!categoryGroups.has(category)) {
                categoryGroups.set(category, []);
            }
            categoryGroups.get(category).push(catalogItem);
        });
        return [...categoryGroups.entries()];
    }, [catalog]);

    /**
     * Updates one field of an item's entry. Low and Expired aren't chosen - they follow from the
     * quantity and expiry date (see the live badges, and the API works them out the same way).
     * @param {Object} catalogItem first_aid_box_items row.
     * @param {string} fieldName isDamaged | quantity | expiryDate | notes
     * @param {*} value New value.
     */
    const handleItemChange = (catalogItem, fieldName, value) => {
        setItemEntries((currentEntries) => ({
            ...currentEntries,
            [catalogItem.id]: { ...currentEntries[catalogItem.id], [fieldName]: value },
        }));
    };

    const handleRecordTag = async () => {
        if (!isTagMissing && !tagSerialFound.trim()) {
            setTagErrors({ tagSerialFound: "Enter the serial on the tag on the box, or select No Tag on the Box" });
            return;
        }

        setIsSubmitting(true);
        try {
            const { data } = await StartFirstAidCheck({
                boxId: box.id,
                eventType,
                isTagMissing,
                tagSerialFound: isTagMissing ? undefined : tagSerialFound.trim(),
            });
            setStartedCheck(data.data);
            setCheckStep(data.data.canFinishOnIntactTag ? CHECK_STEPS.decision : CHECK_STEPS.full);
        } catch (error) {
            console.log("BoxCheckForm.js->handleRecordTag->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The tag could not be recorded."), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinishOnIntactTag = async () => {
        setIsSubmitting(true);
        try {
            const { data } = await FinishFirstAidCheck(startedCheck.openingId);
            await invalidateFirstAidBoxQueries();
            setSubmittedCheck(data.data);
        } catch (error) {
            console.log("BoxCheckForm.js->handleFinishOnIntactTag->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The check could not be finished."), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitFullCheck = async () => {
        if (!newTagSerial.trim()) {
            setTagErrors({ newTagSerial: "Enter the serial on the new tag you fitted" });
            return;
        }

        try {
            const confirmation = await Swal.fire({
                title: "Submit this check?",
                text: `All ${catalog.length} items will be recorded for box ${box.box_code}.`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, submit",
            });
            if (!confirmation.isConfirmed) {
                return;
            }

            setIsSubmitting(true);
            const { data } = await SubmitFirstAidCheck({
                openingId: startedCheck.openingId,
                newTagSerial: newTagSerial.trim(),
                items: catalog.map((catalogItem) => {
                    const itemEntry = itemEntries[catalogItem.id];
                    return {
                        boxItemId: catalogItem.id,
                        isDamaged: Boolean(itemEntry.isDamaged),
                        quantity: Number(itemEntry.quantity) || 0,
                        expiryDate: itemEntry.expiryDate || undefined,
                        notes: itemEntry.notes.trim() || undefined,
                    };
                }),
            });
            await invalidateFirstAidBoxQueries();
            setSubmittedCheck(data.data);
        } catch (error) {
            console.log("BoxCheckForm.js->handleSubmitFullCheck->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The check could not be submitted."), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelButton = (
        <CommonButton type="button" variant="danger" onClick={() => history.push(`${OHS_BASE_PATH}/boxes`)} iconLeft={<i className="fal fa-times"></i>}>
            Cancel
        </CommonButton>
    );

    if (isBoxLoading || isCatalogLoading) {
        return <SkeletonTicketList />;
    }
    if (!box) {
        return <MutedText>This box could not be found.</MutedText>;
    }

    if (submittedCheck) {
        return (
            <ConfirmationPanel>
                <i className="fal fa-check-circle"></i>
                <SectionTitle>Check submitted</SectionTitle>
                <MutedText>
                    {isManagerCheck ? "OHS Rep Check" : "First Aider Check"} of box {box.box_code} recorded on {moment(submittedCheck.checkDate).format("D MMMM YYYY")}.
                </MutedText>
                <ConditionBadge $condition={submittedCheck.condition}>
                    Box condition: {CONDITION_LABELS[submittedCheck.condition]}
                </ConditionBadge>
                {submittedCheck.isTagMismatch && (
                    <TagResultMessage $condition="damaged">
                        The tag on the box didn&apos;t match the one on record - an OHS Representative has been flagged to review it.
                    </TagResultMessage>
                )}
                <WrappingButtonGroup>
                    <Button as={Link} to={`${OHS_BASE_PATH}/boxes`}>Back to Boxes</Button>
                </WrappingButtonGroup>
            </ConfirmationPanel>
        );
    }

    const isAllowed = isManagerCheck ? box.canSubmitManagerCheck : box.canSubmitFirstAiderCheck;
    if (!isAllowed) {
        return (
            <SectionCard>
                <MutedText>
                    {isManagerCheck
                        ? "Only OHS Representatives can perform an OHS Rep Check."
                        : "Only first aiders and OHS Representatives can perform a check."}
                </MutedText>
            </SectionCard>
        );
    }

    return (
        <SectionCard>
            <SectionHeader>
                <div>
                    <SectionTitle>
                        {isManagerCheck ? "OHS Rep Check" : "First Aider Check"}: {box.box_code}
                    </SectionTitle>
                    <MutedText>
                        {box.campus_name} · {box.building_name} · {box.area_location}
                    </MutedText>
                </div>
            </SectionHeader>

            {/* Step 1 - the tag on record is deliberately not shown; the person enters what's physically on the box. */}
            <CategoryHeading>Step 1: Tag on the box</CategoryHeading>
            <FormGrid>
                <TextInput
                    id="ohsTagSerialFound"
                    label="Serial on the tag on the box"
                    required
                    maxLength={60}
                    value={tagSerialFound}
                    onChange={(event) => {
                        setTagSerialFound(event.target.value);
                        setTagErrors({});
                    }}
                    error={Boolean(tagErrors.tagSerialFound)}
                    errorText={tagErrors.tagSerialFound}
                    disabled={Boolean(startedCheck) || isTagMissing}
                />
            </FormGrid>
            <TagStepStack>
                <Checkbox
                    id="ohsTagMissing"
                    label="No Tag on the Box"
                    checked={isTagMissing}
                    onChange={(event) => {
                        setIsTagMissing(event.target.checked);
                        setTagSerialFound("");
                        setTagErrors({});
                    }}
                    disabled={Boolean(startedCheck)}
                />
                {checkStep === CHECK_STEPS.tag && (
                    <WrappingButtonGroup>
                        <Button onClick={handleRecordTag} disabled={isSubmitting}>
                            <i className="fal fa-tag"></i>
                            Record Tag
                        </Button>
                        {cancelButton}
                    </WrappingButtonGroup>
                )}
                {startedCheck && (
                    <TagResultMessage $condition={startedCheck.isTagMismatch || startedCheck.isTagMissing ? "damaged" : "ok"}>
                        <i className={startedCheck.isTagMismatch || startedCheck.isTagMissing ? "fal fa-exclamation-triangle" : "fal fa-check-circle"}></i>
                        {startedCheck.isTagMissing && "No tag on the box - an OHS Representative has been flagged. Do the full check and fit a new tag."}
                        {!startedCheck.isTagMissing && (startedCheck.isTagMismatch
                            ? "This tag doesn't match the one on record - an OHS Representative has been flagged. Do the full check and fit a new tag."
                            : "This tag matches the one on record.")}
                    </TagResultMessage>
                )}
            </TagStepStack>

            {checkStep === CHECK_STEPS.decision && (
                <>
                    <MutedText>
                        The box hasn&apos;t been opened since the last check. Finish now to record its last known contents,
                        or continue to open it and check every item.
                    </MutedText>
                    <WrappingButtonGroup className="mt-3">
                        <PositiveButton onClick={handleFinishOnIntactTag} disabled={isSubmitting} title="Record the check from the box's last known contents - the tag stays on">
                            Finish Check
                        </PositiveButton>
                        <Button variant="secondary" onClick={() => setCheckStep(CHECK_STEPS.full)} disabled={isSubmitting} title="Open the box, check every item and fit a new tag">
                            <i className="fal fa-arrow-right"></i>
                            Continue Check
                        </Button>
                        {cancelButton}
                    </WrappingButtonGroup>
                </>
            )}

            {checkStep === CHECK_STEPS.full && (
                <>
                    {catalogByCategory.map(([category, categoryItems]) => (
                        <div key={category}>
                            <CategoryHeading>{category}</CategoryHeading>
                            {categoryItems.map((catalogItem) => {
                                const itemEntry = itemEntries[catalogItem.id];
                                if (!itemEntry) {
                                    return null;
                                }
                                const isExpired = Boolean(itemEntry.expiryDate) && moment(itemEntry.expiryDate).isBefore(moment(), "day");
                                const liveStatuses = getItemStatuses({
                                    is_low: Number(itemEntry.quantity) < catalogItem.expected_quantity,
                                    is_expired: isExpired,
                                    is_expiring_soon: !isExpired && Boolean(itemEntry.expiryDate)
                                        && !moment(itemEntry.expiryDate).isAfter(moment().add(EXPIRING_SOON_DAYS, "days"), "day"),
                                    is_damaged: itemEntry.isDamaged,
                                });
                                const firstAiderRow = firstAiderRowsByItem.get(catalogItem.id);

                                return (
                                    <CheckItemRow key={catalogItem.id}>
                                        <CheckItemName>
                                            {catalogItem.item_name}
                                            <div><MutedText>Expected {catalogItem.expected_quantity} {catalogItem.unit}</MutedText></div>
                                            <div><ConditionBadges statuses={liveStatuses} /></div>
                                            {isManagerCheck && (
                                                <div>
                                                    <MutedText>
                                                        {firstAiderRow
                                                            ? `First aider (${moment(firstAiderRow.check_date).format("D MMM YYYY")}): ${getItemStatuses(firstAiderRow).map((itemStatus) => CONDITION_LABELS[itemStatus]).join(", ")} · qty ${firstAiderRow.quantity}${firstAiderRow.expiry_date ? ` · exp ${moment(firstAiderRow.expiry_date).format("D MMM YYYY")}` : ""}`
                                                            : "First aider: no report yet"}
                                                    </MutedText>
                                                </div>
                                            )}
                                        </CheckItemName>
                                        <SelectDropdown
                                            id={`ohsCondition${catalogItem.id}`}
                                            aria-label={`${catalogItem.item_name} OK or damaged`}
                                            size="md"
                                            options={ITEM_CONDITION_OPTIONS}
                                            value={itemEntry.isDamaged ? "damaged" : "ok"}
                                            onChange={(event) => handleItemChange(catalogItem, "isDamaged", event.target.value === "damaged")}
                                            isSearchable={false}
                                        />
                                        <NumberInput
                                            aria-label={`${catalogItem.item_name} quantity`}
                                            size="md"
                                            min={0}
                                            value={itemEntry.quantity}
                                            onChange={(quantity) => handleItemChange(catalogItem, "quantity", quantity)}
                                        />
                                        <DateInput
                                            aria-label={`${catalogItem.item_name} expiry date`}
                                            size="md"
                                            value={itemEntry.expiryDate}
                                            onChange={(event) => handleItemChange(catalogItem, "expiryDate", event.target.value)}
                                        />
                                        <TextInput
                                            aria-label={`${catalogItem.item_name} notes`}
                                            size="md"
                                            placeholder="Notes"
                                            value={itemEntry.notes}
                                            onChange={(event) => handleItemChange(catalogItem, "notes", event.target.value)}
                                        />
                                    </CheckItemRow>
                                );
                            })}
                        </div>
                    ))}

                    <CategoryHeading>Step 2: New tag</CategoryHeading>
                    <FormGrid>
                        <TextInput
                            id="ohsNewTagSerial"
                            label="Serial on the new tag you fitted"
                            required
                            maxLength={60}
                            value={newTagSerial}
                            onChange={(event) => {
                                setNewTagSerial(event.target.value);
                                setTagErrors({});
                            }}
                            error={Boolean(tagErrors.newTagSerial)}
                            errorText={tagErrors.newTagSerial}
                        />
                    </FormGrid>

                    <WrappingButtonGroup className="mt-4">
                        <PositiveButton onClick={handleSubmitFullCheck} disabled={isSubmitting}>
                            Submit Check
                        </PositiveButton>
                        {cancelButton}
                    </WrappingButtonGroup>
                </>
            )}
        </SectionCard>
    );
}

export default BoxCheckForm;
